import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
} from '@angular/core';
import {
    FormControl,
    Validators,
} from '@angular/forms';
import {
    IRobot,
    MachineType,
    RobotHostingType,
    RobotType,
} from '@models';
import {
    IRobotFormValue,
    RobotCredentialsControl,
    RobotFormControl,
    RobotModalBaseComponent,
} from '@pages/robots/modal/robot-base.modal';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import { MachineService } from '@services';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';
import { addToObjectIf } from '@utils/object.utils';
import UiValidators, { UI_MACHINE_NAME_MAX_LENGTH } from '@validators';

import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
    styleUrls: ['./robot.modal.scss'],
    templateUrl: './robot-standard.modal.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MachineService],
})
export class StandardRobotModalComponent extends RobotModalBaseComponent implements OnInit {

    public get hostingType() {
        return RobotHostingType.Standard;
    }

    public isMachinesCreateGranted$ = this._appState.user.permissions.isGranted('Machines.Create');
    public isMachinesViewGranted$ = this._appState.user.permissions.isGranted('Machines.View');

    private _machineService = this._injector.get<MachineService>(MachineService);

    public get formTitle() {
        return this._translateService.instant('robot_modal_form_standard_title');
    }

    public get formDescription() {
        return this._translateService.instant('robot_modal_form_standard_info_text');
    }

    ngOnInit() {
        super.ngOnInit();

        this.robotForm.addControl(
            RobotFormControl.machine,
            new FormControl(
                this.context.data.machineName ? [{
                    id: this.context.data.machineId,
                    text: this.context.data.machineName,
                }] : [],
                Validators.compose([
                    UiValidators.required(),
                    UiValidators.maxLength(UI_MACHINE_NAME_MAX_LENGTH, {
                        property: 'text',
                    }),
                ]),
            ),
        );
    }

    public translateMachineLabel = (value: string) =>
        this._translateService.instant('robot_modal_form_machine_custom_value', { value })

    public machineSearchFactory = (query: string, step: number = MAX_NUMBER_OF_RESULTS, skip: number = 0) =>
        this.isMachinesViewGranted$
            .pipe(
                switchMap(
                    isGranted => isGranted && this.context.isNewEntry ?
                        this._machineService.machineNameSuggest(query, step, skip, MachineType.Standard) :
                        of({
                            data: [],
                            total: 0,
                        }),
                ),
            )

    protected _formValueToPayload(formValue: IRobotFormValue) {
        const payload = super._formValueToPayload(formValue);

        const [selectedMachineEntry] = this.robotForm.get(RobotFormControl.machine)!.value as ISuggestValue[];

        return {
            ...payload,
            machineId: selectedMachineEntry.id !== selectedMachineEntry.text ? selectedMachineEntry.id : null,
            machineName: selectedMachineEntry.text,
        };
    }

    protected _getFormValueToCreateAnother = (robot: IRobot) => ({
        [RobotFormControl.hostingType]: robot.hostingType,
        [RobotFormControl.type]: [this._robotTypeToDropdownEntry(robot.type)],
        [RobotFormControl.isExternalLicense]: !!robot.isExternalLicense,
        ...addToObjectIf(
            robot.type !== RobotType.Attended, {
                [RobotFormControl.robotCredentials]: {
                    [RobotCredentialsControl.credentialStore]: [this._credentialStoreToDropdownEntry(this.selectedCredentialStore)],
                },
            }),
        [RobotFormControl.machine]: [{
            id: robot.machineId,
            text: robot.machineName,
        }],
    })
}
